import Vue from 'vue';

Vue.component('configurator-accessories-select', {
    props: {
        locale: {
            type: String,
        },
    },

    data() {
        return {
            activeAccessoriesOptions: null,
            selectionExpanded: false,
            isAnyAccessorySelected: false,
            isSubSelectionExpanded: false,
            selectedAccessories: [],
            currentlyExcludedAccessories: [],
        };
    },

    mounted() {
        // Get active accessories every time payment option changes.
        EventBus.$on(
            'configurator-set-accessories-options',
            (accessoriesOptions) => {
                this.selectedAccessories = [];
                this.activeAccessoriesOptions = accessoriesOptions;
                this.checkForAccessoriesSelectedByDefault(accessoriesOptions);
            },
        );
    },

    watch: {
        // Emit changed selectedAccessories on every change to update the price in payment options component.
        selectedAccessories(newVal, oldVal) {
            EventBus.$emit('configurator-accessories-options-change', newVal);
        },
    },

    computed: {
        toggleConfiguratorBoxAccessories() {
            return this.selectionExpanded ? 'active' : '';
        },
    },

    methods: {
        handleSelectionExpand() {
            this.selectionExpanded = !this.selectionExpanded;
        },

        handleClassSelectionExpand() {
            return this.canOpen ? '' : 'configurator__select-title--no-pointer';
        },

        toggleSwitchButton(handle) {
            return this.isAccessorySelected(handle)
                ? 'switch-button--selected'
                : '';
        },

        // Deselect options that the selected option has in its "excludes" field and update selectedAccessories.
        checkForExcludedOptions(accessory) {
            const allAccessories = [...this.selectedAccessories];
            const allExcludedHandles = accessory.excludes.map(
                (tag) => tag.title,
            );

            const filteredExcludedAccessories = allAccessories.filter(
                (option) =>
                    !allExcludedHandles.some(
                        (handle) => handle === option.accessoryHandle,
                    ),
            );

            this.selectedAccessories = filteredExcludedAccessories;
        },

        handleSelectAccessory(option) {
            const isSimilarAccessorySelected = this.selectedAccessories.some(
                (accessory) =>
                    accessory.accessoryHandle === option.accessoryHandle,
            );

            // Deselect the option if it's already active and remove it from selectedAccessories array.
            if (isSimilarAccessorySelected) {
                const accessoriesWithRemovedOption =
                    this.selectedAccessories.filter((accessory) => {
                        return (
                            accessory.accessoryHandle !== option.accessoryHandle
                        );
                    });

                this.selectedAccessories = accessoriesWithRemovedOption;
                return;
            }

            const selectedOption = {
                accessoryName: option.accessoryName,
                accessoryHandle: option.accessoryHandle,
                price: option.price,
                priceVat: option.priceVat,
                productCode: option.productCode,
                subOption: Boolean(option.options?.length)
                    ? option.options[0]
                    : null,
                priceImpactInactive: option.priceImpactInactive,
                excludes: option.excludes,
                priceOverruleText: option.priceOverruleText,
            };

            this.selectedAccessories.push(selectedOption);
            this.checkForExcludedOptions(selectedOption);
            this.toggleSwitchButton(selectedOption.accessoryHandle);
        },

        handleSelectAccessorySubOption(accessoryOption, subOption) {
            const selectedAccessory = this.selectedAccessories.find(
                (accessory) =>
                    accessory.accessoryHandle ===
                    accessoryOption.accessoryHandle,
            );

            selectedAccessory.subOption = subOption;

            this.checkForExcludedOptions(selectedAccessory);
            EventBus.$emit(
                'configurator-accessories-options-change',
                this.selectedAccessories,
            );
        },

        isAccessorySelected(handle) {
            return this.selectedAccessories.some(
                (option) => option.accessoryHandle === handle,
            );
        },

        isAccessorySubOptionSelected(accessoryHandle, subOptionHandle) {
            const accessoryWithOptions = this.selectedAccessories.find(
                (accessory) => accessory.accessoryHandle === accessoryHandle,
            );

            return (
                accessoryWithOptions.subOption?.optionHandle === subOptionHandle
            );
        },

        toggleRadioButton(accessoryHandle, subOptionHandle) {
            return this.isAccessorySubOptionSelected(
                accessoryHandle,
                subOptionHandle,
            )
                ? 'radio-button-list__button--selected'
                : '';
        },

        getOptionPriceText(price, overruleText) {
            return overruleText
                ? overruleText
                : `€ ${this.formatAsPrice(price)}`;
        },

        formatAsPrice(price) {
            // Set localization formatting.
            let countyPriceMapping = {
                localization: 'de-DE',
                showZeros: false,
            };
            if (this.$parent.countyPriceMapping[this.locale] != undefined) {
                countyPriceMapping =
                    this.$parent.countyPriceMapping[this.locale];
            }

            // Format with 2 decimals if price as any decimals.
            if (price % 1 != 0 || countyPriceMapping.showZeros) {
                return price.toLocaleString(countyPriceMapping.localization, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
            }

            // Format with no decimal digits.
            return price.toLocaleString(countyPriceMapping.localization);
        },

        checkForAccessoriesSelectedByDefault(availableAccessories) {
            const accessoriesSelectedByDefault = availableAccessories
                .map((accessory) => ({
                    accessoryName: accessory.accessoryName,
                    accessoryHandle: accessory.accessoryHandle,
                    price: accessory.price,
                    priceVat: accessory.priceVat,
                    priceOverruleText: accessory.priceOverruleText,
                    productCode: accessory.productCode,
                    subOption:
                        accessory.options.find(
                            (option) => option.selectedByDefault,
                        ) ?? null,
                    priceImpactInactive: accessory.priceImpactInactive,
                    excludes: accessory.excludes,
                    selectedByDefault: accessory.selectedByDefault,
                    isDisabled: accessory.isDisabled,
                }))
                .filter(
                    (accessory) =>
                        accessory.isDisabled ||
                        accessory.selectedByDefault ||
                        Boolean(accessory.subOption),
                );

            const filteredExcludedAccessories = [];
            const excludedSlugs = new Set();

            // Filters out mutually exclusive options (from "excludes" field) that are selected by default, keeping only the first one.
            accessoriesSelectedByDefault.forEach((accessory) => {
                if (!excludedSlugs.has(accessory.accessoryHandle)) {
                    filteredExcludedAccessories.push(accessory);

                    if (Boolean(accessory.excludes.length)) {
                        accessory.excludes.forEach((tag) =>
                            excludedSlugs.add(tag.title),
                        );
                    }
                }
            });

            this.selectedAccessories = filteredExcludedAccessories;
            this.selectionExpanded = true;
        },
    },
});
